/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import parse from 'parse-json';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import Manager from '../Manager/Manager';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import managersContainerStyles, { themeManagerContainerStyles } from './ManagersContainer.styles';

interface DynamicEvent {
    eventName: string;
    customEventName?: string;
    analyticItems: AnalyticsItem[];
}

interface AnalyticsItem {
    name: string;
    value: string;
}
interface ManagersContainerProps {
    dataKey: string;
    managerInfo: string;
    containerTitles: Headings[];
    inNewTab: boolean;
    linkTarget?: string;
    linkIcon: string;
    isCompany: boolean;
    isImgLink: boolean;
    imageLink: string;
    localeSupport: boolean;
    teamLink: string;
    isBioReadMore: boolean;
    investmentsExperienceDetails: boolean;
    readMoreText: string;
    investmentsDetails: string;
    readLessText: string;
    numberOfCharacters: number;
    altText: string;
    typographyClass?: string;
    imageType?: string;
    managerBioHtml?: string;
    managerNameHtml?: string;
    groupCompany?: boolean;
    isAccordion?: boolean;
    accordionIconType?: string;
    accordionIconOpenName?: string;
    accordionIconClosingName?: string;
    accordionIconOpenImage?: string;
    accordionIconClosingImage?: string;
    openFirstAccordion?: boolean;
    isAddPlaceholder?: boolean;
    disableTabStaticData?: boolean;
    isTransformData?: boolean;
    functionToTransform: string;
    useAnalytics?: boolean;
    dynamicEvents?: DynamicEvent[];
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, ManagersContainerProps {}

const ManagersContainer = (props: commonTypes.WidgetInstanceProps) => {
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { disableTabStaticData: globalDisableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const {
        styles,
        dataKey,
        managerInfo,
        containerTitles,
        linkIcon,
        inNewTab,
        linkTarget,
        isCompany,
        isImgLink,
        imageLink,
        localeSupport,
        teamLink,
        isBioReadMore,
        investmentsExperienceDetails,
        readMoreText,
        investmentsDetails,
        readLessText,
        numberOfCharacters,
        altText,
        imageType,
        managerBioHtml,
        managerNameHtml,
        groupCompany,
        useAnalytics,
        dynamicEvents,
        ...rest
    } = widgetProperties || {};
    const { data } = props;
    const i18n = commonHooks.useLocalization();
    const { value: managersData }: any = JsonQuery(`${dataKey}`, { data });

    const widgetInstance = commonHooks.useWidgetInstance(props.widgetInstanceId);
    const widgetInstanceId = widgetInstance?.id;
    const sortedManagersData: any = Array.isArray(managersData)
        ? parse(JSON.stringify(managersData)).sort((a: any, b: any) => {
              return a.leadOrder - b.leadOrder;
          })
        : [];
    const managersIndustryExperienceDates: string[] = [];
    sortedManagersData?.length &&
        sortedManagersData.forEach((m: any) => {
            if (m.manager?.industryExperienceSince) {
                managersIndustryExperienceDates.push(m.manager?.industryExperienceSince);
            }
        });

    const experienceBaseDate = managersIndustryExperienceDates.length
        ? managersIndustryExperienceDates.sort((a: string, b: string) => {
              const aDate: any = new Date(a);
              const bDate: any = new Date(b);
              return aDate - bDate;
          })[0]
        : '';

    const teamLinkHtml = i18n.format(teamLink || '', data);
    const resourceStrings: any = data?.resourceStrings;
    const theme = data?.themeSelected || props?.theme;
    const managersByCompany: { [key: string]: any[] } = {};
    let displayedManagersData: { company: string; managers: any[] }[] = [];

    // Group managers by company
    if (groupCompany && sortedManagersData?.length > 0) {
        sortedManagersData.forEach((item: any) => {
            const company = item.manager.company;

            if (!managersByCompany[company]) {
                // Initialize array for the company if it doesn't exist
                managersByCompany[company] = [];
            }
            // Push the manager object into the appropriate company array
            managersByCompany[company].push(item);
        });
        displayedManagersData = Object.entries(managersByCompany).reduce<
            { company: string; managers: any[] }[]
        >((acc, [company, managers]) => {
            acc.push({ company, managers });
            return acc;
        }, []);
    }

    let isRender = {
        transformedData: data,
        sortedManagersData,
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const transformedData =
            rest?.isTransformData &&
            commonUtils.transformDataWithGlobals(transformFunctionName, rest?.functionToTransform, {
                ...data,
                sortedManagersData,
            });

        if (transformedData) {
            isRender = { ...isRender, ...transformedData };
        }
    } catch (e) {
        console.log(e);
    }

    // Added here to avoid having to pass it down to the Manager component
    const managersComponent = (m: any, _altText: string, resourceStrings: any, i: number) => (
        <Manager
            key={m.id + i}
            link={m.manager?.biographyUrl || m.manager?.companyUrl}
            linkName={
                (isCompany
                    ? m.manager?.company
                        ? `${resourceStrings.learnMore || 'Learn More About'} ${m.manager?.company}`
                        : ''
                    : managerBioHtml || m.manager?.biography) || ''
            }
            managerName={
                managerNameHtml ||
                (m.manager?.fullName
                    ? `${m.manager?.fullName || ''}`
                    : `${m.manager?.firstName || ''} ${m.manager?.lastName || ''}`)
            }
            managerInfo={managerInfo}
            managerBio={managerBioHtml || m.manager?.biography || ''}
            isCompany={isCompany}
            isImgLink={isImgLink}
            imageLink={
                localeSupport
                    ? `${imageLink}/${m.manager?.entityId}_${data?.lang}.${imageType || 'jpg'}`
                    : `${imageLink}/${m.manager?.entityId}.${imageType || 'jpg'}`
            }
            imgFileName={m.manager?.entityId || undefined}
            imgFileType={'jpg'}
            altText={_altText || ''}
            openInNewTab={inNewTab}
            linkTarget={linkTarget}
            iconName={linkIcon || ''}
            companyName={m.manager?.company || m.manager?.fullName || ''}
            companyDetails={m.manager?.biography || ''}
            investmentsDetails={investmentsDetails}
            investmentsExperienceDetails={investmentsExperienceDetails}
            data={{
                ...m,
                resourceStrings: data?.resourceStrings,
                themeSelected: theme,
            }}
            experienceBaseDate={experienceBaseDate}
            isBioReadMore={isBioReadMore}
            readMoreText={readMoreText}
            readLessText={readLessText}
            numberOfCharacters={numberOfCharacters}
            isAccordion={rest?.isAccordion}
            accordionIconType={rest?.accordionIconType}
            accordionIconOpenName={rest?.accordionIconOpenName}
            accordionIconClosingName={rest?.accordionIconClosingName}
            accordionIconOpenImage={rest?.accordionIconOpenImage}
            accordionIconClosingImage={rest?.accordionIconClosingImage}
            openFirstAccordion={i === 0 ? rest?.openFirstAccordion : false}
            isAddPlaceholder={rest?.isAddPlaceholder}
            widgetInstanceParentId={widgetInstanceId}
            useAnalytics={useAnalytics}
            dynamicEvents={dynamicEvents}
        />
    );

    return (
        <>
            {isRender.sortedManagersData?.length > 0 && (
                <div
                    className={`managers-container ${
                        widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                    }`}
                    css={css`
                        ${!theme
                            ? managersContainerStyles
                            : themeManagerContainerStyles}${getTextSchemaProperties(
                            widgetProperties,
                            data?.colorSpace
                        )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                            widgetProperties
                        )}${styles || ''}
                    `}
                >
                    <HeaderWrapper
                        headings={containerTitles}
                        data={data}
                        disableTabStaticData={
                            globalDisableTabStaticData || rest.disableTabStaticData
                        }
                    />
                    <div
                        className="management-team-link"
                        dangerouslySetInnerHTML={{ __html: teamLinkHtml || '' }}
                    />

                    {groupCompany &&
                        displayedManagersData.map((group: any, i: number) => {
                            const company = group.company;
                            return (
                                <div key={company + i} className={'company-name'}>
                                    {groupCompany && <h2>{company || ''}</h2>}
                                    <div
                                        className="manager-widgets-container"
                                        data-testid={'manager-widgets-container'}
                                    >
                                        {group.managers.map((m: any, _j: any) => {
                                            const _altText = i18n.format(altText || '', {
                                                ...data,
                                                ...m,
                                            });
                                            return managersComponent(
                                                m,
                                                _altText,
                                                resourceStrings,
                                                i
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}

                    {!groupCompany && (
                        <div
                            className="manager-widgets-container"
                            data-testid={'manager-widgets-container'}
                        >
                            {isRender.sortedManagersData.map((m: any, i: number) => {
                                const _altText = i18n.format(altText || '', {
                                    ...data,
                                    ...m,
                                });
                                return managersComponent(m, _altText, resourceStrings, i);
                            })}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default ManagersContainer;
